import { SnapLineContext } from '@src/slide/application/service/snap-line-service/context';
import { SnapLineDetector } from '@src/slide/application/service/snap-line-service/detector';
import { SnapLineAdsorber } from '@src/slide/application/service/snap-line-service/adsorber';
import {
    DragEventType,
} from '@src/slide/application/service/edit-action-services/drag-element-handler/drag-element-handler.interface';
import {
    ResizeEventType,
} from '@src/slide/application/service/edit-action-services/resize-element-handler/resize-element-handler.interface';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import {
    OperationType,
} from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import { SnapLineCommitter } from '@src/slide/application/service/snap-line-service/committer';
import { observable } from 'mobx';
import {
    EnableSnapLineState,
    ENABLE_SNAP_LINE_STATE_LOCAL_KEY,
} from '@src/slide/application/service/snap-line-service/constant';
import type { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import type {
    IEditAreaCanvasSizeManager,
} from '@src/slide/application/service/edit-area-canvas-size-manager/edit-area-canvas-size-manager.interface';
import type {
    ISnapLineService,
    ProcessingEventPayload,
} from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import type { IActiveSnapGridLine } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import type {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type {
    IResizeElementHandler,
    IResizingEventPayload,
} from '@src/slide/application/service/edit-action-services/resize-element-handler/resize-element-handler.interface';
import type {
    IDragElementHandler,
    IDraggingEventPayload,
    IDragStartEventPayload,
} from '@src/slide/application/service/edit-action-services/drag-element-handler/drag-element-handler.interface';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';


export class SnapLineService implements ISnapLineService {
    public readonly context: SnapLineContext;
    public readonly detector: SnapLineDetector;
    public readonly adsorber: SnapLineAdsorber;
    public readonly committer: SnapLineCommitter;

    @observable public enableState: boolean;

    public constructor(
        private dragElementHandler: IDragElementHandler,
        private resizeElementHandler: IResizeElementHandler,
        private selectionService: ISelectionService,
        private editAreaCanvasSizeManager: IEditAreaCanvasSizeManager,
        private menuManager: IMenuManager,
        private uiDataService: IUiDataService,
    ) {
        this.context = new SnapLineContext(selectionService, uiDataService, editAreaCanvasSizeManager, menuManager);
        this.detector = new SnapLineDetector(selectionService, uiDataService, this.context);
        this.adsorber = new SnapLineAdsorber(this.context);
        this.committer = new SnapLineCommitter(this.context);

        const enableState = userLocalStorage.getItem(
            LocalStorageKeys.ENABLE_SNAP_LINE_STATE,
            EnableSnapLineState.ENABLE,
        );
        this.enableState = enableState === EnableSnapLineState.ENABLE;

        this.subscribeServiceEvents();
    }

    /**
     * 设置是否开启智能吸附，并缓存状态
     */
    public toggleSnapLine() {
        this.enableState = !this.enableState;
        userLocalStorage.setItem(
            LocalStorageKeys.ENABLE_SNAP_LINE_STATE,
            this.enableState ? EnableSnapLineState.ENABLE : EnableSnapLineState.DISABLE,
        );
    }

    public handleNotifyEvent(payload: ProcessingEventPayload) {
        if (payload.mode === MODES.MOVE) {
            this.handleDragging(payload as IDraggingEventPayload);
        } else {
            this.handleResizing(payload as IResizingEventPayload);
        }
    }

    public getActiveSnapLines(): IActiveSnapGridLine[] {
        return this.context.activeSnapLines || [];
    }

    private subscribeServiceEvents() {
        this.subscribeDragEvents();
        this.subscribeResizeEvents();
    }

    private subscribeDragEvents() {
        this.dragElementHandler.on(DragEventType.DRAG_START, this.handleDragStart);
        // 目前吸附因为一些时序问题，暂时还需要依赖 edit-process 中去触发，而不能只依赖事件。后续看看如何优化这里
        // this.dragElementHandler.on('dragging', this.handleDragging);
        this.dragElementHandler.on(DragEventType.DRAG_STOP, this.handleDragStop);
    }

    private subscribeResizeEvents() {
        this.resizeElementHandler.on(ResizeEventType.RESIZE_START, this.handleResizeStart);
        // 目前吸附因为一些时序问题，暂时还需要依赖 edit-process 中去触发，而不能只依赖事件。后续看看如何优化这里
        // this.resizeElementHandler.on('resizing', this.handleResizing);
        this.resizeElementHandler.on(ResizeEventType.RESIZE_STOP, this.handleResizeStop);
    }

    private handleDragStart = ({ elementModel, e }: IDragStartEventPayload) => {
        if (!this.enableState) return;

        this.initContext(elementModel, e);
    };

    private handleDragging = (payload: IDraggingEventPayload) => {
        if (!this.enableState) return;

        this.setContextDetail(payload, OperationType.MOVE);
        this.dynamicAdsorb();
    };

    private handleDragStop = () => {
        this.reset();
    };

    private handleResizeStart = ({ elementModel, e }: IDragStartEventPayload) => {
        if (!this.enableState) return;

        this.initContext(elementModel, e);
    };

    private handleResizing = (payload: IResizingEventPayload) => {
        if (!this.enableState) return;

        this.setContextDetail(payload, OperationType.RESIZE);
        this.dynamicAdsorb();
    };

    private handleResizeStop = () => {
        this.reset();
    };

    private setContextDetail(payload: ProcessingEventPayload, operationType: OperationType) {
        this.context.setDetail(payload, operationType);
    }

    /**
     * 初始化上下文
     */
    private initContext(elementModel: IElementBaseModel, e: any) {
        this.context.init(elementModel, e);
    }

    /**
     * 重置上下文和探测器
     */
    private reset() {
        this.context.reset();
        this.detector.reset();
    }

    /**
     * 进行动态参考线吸附
     */
    private dynamicAdsorb() {
        const { detectFrame } = this.context;
        /* 更新动态上下文 */
        this.context.update(detectFrame);
        /* 用当前元素的新 frame 进行参考线吸附探测 */
        const activeSnapLines = this.detector.detect();
        /* 进行吸附操作 */
        const adsorbResult = this.adsorber.adsorb(activeSnapLines);
        /* 提交 patch  */
        this.committer.commit(adsorbResult);
        /* 更新触发的吸附线，通知 UI 进行渲染  */
        this.context.setActiveSnapLines(activeSnapLines, adsorbResult?.afterAdsorbFrame);
    }
}
